import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Sparkles,
  Route as RouteIcon,
  FileSignature,
  HelpCircle,
  ShieldAlert,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TrustSection from "@/components/TrustSection";
import SEO, { CANONICAL_ORIGIN } from "@/components/SEO";
import { SEO_PAGES } from "@/data/seoPages";
import { getWhatToDo, getDocs, getRelated, getCategoryLabel } from "@/data/seoContent";

const HELP_CARDS = [
  { icon: ClipboardList, title: "Sorunu anlar", text: "Kartlar üzerinden sorununuzu ve ilgili kurumu belirleyerek süreç başlar." },
  { icon: RouteIcon, title: "Doğru yolu seçer", text: "Yasal çerçeve ve pratik iş akışına göre size en uygun adımları çıkarır." },
  { icon: FileSignature, title: "Hazır metni oluşturur", text: "Türkçe başvuru metnini, belge listesini ve takip planını hazırlar." },
];

export default function SeoLanding({ config }) {
  const navigate = useNavigate();
  const goStart = () => {
    const q = new URLSearchParams();
    if (config.prefill?.issue) q.set("issue", config.prefill.issue);
    if (config.prefill?.company) q.set("company", config.prefill.company);
    navigate(`/dosya-baslat?${q.toString()}`);
  };

  const canonicalPath = `/${config.slug}`;
  const whatToDo = getWhatToDo(config);
  const docs = getDocs(config);
  const related = getRelated(SEO_PAGES, config);
  const catLabel = getCategoryLabel(config.category);

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: config.metaTitle || config.title,
      description: config.metaDescription || config.lead,
      url: `${CANONICAL_ORIGIN}${canonicalPath}`,
      inLanguage: "tr-TR",
      isPartOf: { "@type": "WebSite", name: "Abonelik İptal", url: CANONICAL_ORIGIN },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: `${CANONICAL_ORIGIN}/` },
        { "@type": "ListItem", position: 2, name: config.title, item: `${CANONICAL_ORIGIN}${canonicalPath}` },
      ],
    },
  ];
  if (config.faq && config.faq.length > 0) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: config.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <div>
      <SEO
        title={config.metaTitle || `${config.title} | Abonelik İptal`}
        description={config.metaDescription || config.lead}
        path={canonicalPath}
        jsonLd={jsonLd}
      />
      <Header />

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-10">
        {catLabel && (
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#14A89F] mb-3">
            {catLabel}
          </div>
        )}
        <h1
          data-testid="seo-title"
          className="text-4xl md:text-5xl font-bold text-[#0F172A] tracking-tight max-w-3xl leading-[1.05]"
        >
          {config.title}
        </h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-3xl">
          {config.lead}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <button
            data-testid="seo-cta-btn"
            onClick={goStart}
            className="fs-btn-primary inline-flex items-center gap-2"
          >
            {config.ctaLabel} <ArrowRight size={18} />
          </button>
          <Link to="/nasil-calisir" className="fs-btn-outline">
            Nasıl Çalışır?
          </Link>
        </div>
      </section>

      {/* WHAT TO DO */}
      <section
        data-testid="seo-what-to-do"
        className="max-w-5xl mx-auto px-5 md:px-8 mt-4"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
          Bu durumda ne yapılır?
        </h2>
        <div className="mt-6 space-y-3">
          {whatToDo.map((step, i) => (
            <div
              key={i}
              data-testid={`seo-step-${i}`}
              className="fs-card p-5 md:p-6 flex gap-4 items-start"
            >
              <div className="w-9 h-9 rounded-2xl bg-[#0F172A] text-white flex items-center justify-center text-sm font-bold shrink-0">
                {i + 1}
              </div>
              <div>
                <div className="text-lg font-semibold text-[#0F172A]">{step.title}</div>
                <div className="mt-1 text-slate-600 leading-relaxed">{step.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* HELP CARDS */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-14">
        <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
          Abonelik İptal nasıl yardımcı olur?
        </h2>
        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {HELP_CARDS.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} data-testid={`seo-help-${i}`} className="fs-card p-6">
                <div className="w-11 h-11 rounded-2xl bg-teal-50 flex items-center justify-center">
                  <Icon size={20} className="text-[#0F8F87]" strokeWidth={2.25} />
                </div>
                <div className="mt-4 text-base font-semibold text-[#0F172A]">
                  {h.title}
                </div>
                <div className="mt-1 text-sm text-slate-600 leading-relaxed">
                  {h.text}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* REQUIRED DOCS */}
      <section
        data-testid="seo-docs"
        className="max-w-5xl mx-auto px-5 md:px-8 mt-14"
      >
        <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
          Gerekli bilgiler ve belgeler
        </h2>
        <ul className="mt-6 space-y-2.5">
          {docs.map((d, i) => (
            <li
              key={i}
              data-testid={`seo-doc-${i}`}
              className="flex items-start gap-3 rounded-2xl bg-white border border-slate-200 p-4"
            >
              <div className="w-6 h-6 rounded-full bg-[#14A89F] text-white text-xs flex items-center justify-center mt-0.5 shrink-0">
                <CheckCircle2 size={14} />
              </div>
              <span className="text-slate-800">{d}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* PRIMARY CTA */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 mt-14">
        <div className="rounded-3xl bg-[#0F172A] text-white p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-5 md:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#22D3CE] mb-2">
              Hazır mısınız?
            </div>
            <div className="text-2xl md:text-3xl font-bold flex items-center gap-2">
              <Sparkles size={22} className="text-[#22D3CE]" />
              Hazır metni ve takip planınızı oluşturun.
            </div>
          </div>
          <button
            data-testid="seo-cta-bottom-btn"
            onClick={goStart}
            className="fs-btn-primary inline-flex items-center gap-2 whitespace-nowrap"
          >
            {config.ctaLabel} <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* FAQ */}
      {config.faq && config.faq.length > 0 && (
        <section
          data-testid="seo-faq"
          className="max-w-5xl mx-auto px-5 md:px-8 mt-14"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
            Sık sorulan sorular
          </h2>
          <div className="mt-6 space-y-3">
            {config.faq.map((f, i) => (
              <div
                key={i}
                data-testid={`seo-faq-${i}`}
                className="fs-card p-5 md:p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-teal-50 flex items-center justify-center shrink-0">
                    <HelpCircle size={16} className="text-[#0F8F87]" />
                  </div>
                  <div>
                    <div className="text-base md:text-lg font-semibold text-[#0F172A]">
                      {f.q}
                    </div>
                    <div className="mt-1.5 text-slate-600 leading-relaxed">
                      {f.a}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <section
          data-testid="seo-related"
          className="max-w-5xl mx-auto px-5 md:px-8 mt-14"
        >
          <h2 className="text-xl font-bold text-[#0F172A]">İlgili konular</h2>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`seo-related-${r.slug}`}
                className="rounded-full bg-white border border-slate-200 px-4 py-2 text-sm font-semibold text-[#0F172A] hover:border-[#14A89F] transition-colors"
              >
                {r.label}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <div className="max-w-5xl mx-auto px-5 md:px-8 mt-10 flex items-start gap-2 text-xs text-slate-500">
        <ShieldAlert size={14} className="mt-0.5 shrink-0" />
        <span>
          Abonelik İptal resmi kurum değildir ve hukuki danışmanlık vermez. Hazırlanan
          metinleri göndermeden önce siz kontrol edersiniz.
        </span>
      </div>

      <TrustSection />
      <Footer />
    </div>
  );
}
